import {
  Card,
  Col,
  Descriptions,
  Row,
  Tag,
  Typography,
  Popover,
  Modal,
} from "antd";
import popOverIcon from "../../../../assets/images/reject-reason-popover.svg";
import React, { lazy, Suspense, useEffect, useState } from "react";
import styles from "./ViewInternalApproval.module.scss";
import clsx from "clsx";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import CreateJobServices from "../../../../services/createJob.services";
import { SectionStatus } from "../../../../enums/SubSectionStatus";
import { UploadDocType } from "../../CreateJobView/JobLead/constants";
import { ImageAltText, ModalTitle, ModalWidth } from "./constants";
import { DocTypes } from "../../../../enums/DocTypes";
import { PlacementPositions } from "../../../../enums/PlacementPositionsEnums";
import { internalApprovalHeaders } from "../../CreateJobView/InternalApproval/constants";
import fileIcon from "../../../../assets/images/default_file.svg";
import { FileTypes } from "../../../../enums/FileUploadEnums";
import TNButton from "../../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonType,
  CommonButtonTexts,
} from "../../../../enums/ButtonAttributesEnums";
import { downloadUtil } from "../../../../utils/downloadUtil";
import { iconMapping, ViewJobConstants } from "../constants";
const LazyDocumentViewer = lazy(
  () => import("../../../../reusable-components/MultiFileViewer"),
);

const ViewInternalApproval = ({ jobLead }) => {
  const { Title } = Typography;
  const { pdfAltText } = ImageAltText;
  const { 
    CASH_OR_CREDIT_DOCS_TITLE,
    NO_DOCUMENTS_TITLE,
    GENERAL_PROJECT_DOCS_TITLE,
  } = ViewJobConstants;
  const approvalDocuments = jobLead?.approvalDocuments ?? [];
  const cashCreditProjectDocuments = jobLead?.cashOrCreditProjectDocs;
  const { FetchAllDocuments } = CreateJobServices();
  const { data: documentsData } = FetchAllDocuments(jobLead?.id);
  const subSections = [
    "Project Info Section",
    "Property Legal Description",
    "Property Owner Information",
    "General Contractor Information",
    "Subcontractor Information",
    "Bond Information",
  ];

  const [projectBondPreviewOpen, setProjectBondPreviewOpen] =
    useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState(0);
  const [projectInfoSectionFiles, setProjectInfoSectionFiles] = useState([]);
  const [propertyLegalDescriptionFiles, setPropertyLegalDescriptionFiles] =
    useState([]);

  const [propertyOwnerInformationFiles, setPropertyOwnerInformationFiles] =
    useState([]);
  const [
    generalContractorInformationFiles,
    setGeneralContractorInformationFiles,
  ] = useState([]);

  const [subcontractorInformationFiles, setSubcontractorInformationFiles] =
    useState([]);
  const [projectBondFiles, setProjectBondFiles] = useState([]);

  const projectInformationSheet = documentsData?.find(
    (doc) => doc?.docType === UploadDocType.projectDoc,
  );
  const bondInfoDocument = documentsData?.find(
    (doc) => doc?.docType === DocTypes.BOND_INFO_DOCUMENT,
  );
  const openProjectBondPreview = () => setProjectBondPreviewOpen(true);
  const closeProjectBondPreview = () => setProjectBondPreviewOpen(false);
  const getOverallApprovalStatus = () => {
    if (!approvalDocuments) return SectionStatus.PENDING;
    const statuses = Object?.values(approvalDocuments)?.map(
      (section) => section?.status,
    );
    const hasRejected = statuses?.some(
      (status) =>
        status === SectionStatus.L1_REJECTED ||
        status === SectionStatus.L2_REJECTED,
    );

    if (hasRejected) return SectionStatus.REJECTED;

    const allApproved = statuses?.every(
      (status) =>
        status === SectionStatus.L1_APPROVED ||
        status === SectionStatus.L2_APPROVED,
    );
    if (allApproved) return SectionStatus.APPROVED;

    const allPending = statuses?.every(
      (status) =>
        status === SectionStatus.PENDING || status === SectionStatus.PENDING,
    );
    if (allPending) return SectionStatus.PENDING;
    return SectionStatus.PENDING;
  };

  const {
    projectDetailsHeader,
    onSiteContactHeader,
    projectCoordinatesHeader,
    projectBondStatusHeader,
    invoiceNotesHeader,
    invoiceNotesSubHeader,
    notBondedFallBack,
  } = internalApprovalHeaders;

  useEffect(() => {
    if (
      !!!approvalDocuments &&
      !!!cashCreditProjectDocuments &&
      !!!documentsData
    )
      return;

    const getDocsByIds = (ids) =>
      documentsData?.filter((doc) => ids?.includes(doc?.id));
    if (!!cashCreditProjectDocuments) {
      setProjectInfoSectionFiles(
        getDocsByIds(cashCreditProjectDocuments) || [],
      );
      return;
    }
    setProjectInfoSectionFiles(
      getDocsByIds(
        approvalDocuments[DocTypes.PROJECT_INFO_SECTION]?.documentAttachments ||
          [],
      ),
    );
    setPropertyLegalDescriptionFiles(
      getDocsByIds(
        approvalDocuments[DocTypes.PROPERTY_LEGAL_DESCRIPTION]
          ?.documentAttachments || [],
      ),
    );
    setPropertyOwnerInformationFiles(
      getDocsByIds(
        approvalDocuments[DocTypes.PROPERTY_OWNER_INFORMATION]
          ?.documentAttachments || [],
      ),
    );
    setGeneralContractorInformationFiles(
      getDocsByIds(
        approvalDocuments[DocTypes.GENERAL_CONTRACTOR_INFORMATION]
          ?.documentAttachments || [],
      ),
    );
    setSubcontractorInformationFiles(
      getDocsByIds(
        approvalDocuments[DocTypes.SUB_CONTRACTOR_INFORMATION]
          ?.documentAttachments || [],
      ),
    );
    setProjectBondFiles(
      getDocsByIds(
        approvalDocuments[DocTypes.BOND_INFORMATION]?.documentAttachments || [],
      ),
    );
  }, [approvalDocuments, cashCreditProjectDocuments, documentsData]);

  return (
    <>
      <div className={styles.TopContainer}>
        <Card>
          <Row gutter={24}>
            <Col span={12}>
              <Title level={5}>{projectDetailsHeader}</Title>
              <Descriptions column={1} size="small" colon={false}>
                <Descriptions.Item
                  label="Dropdown 1"
                  className={styles.TopContainer__Description}
                >
                  {jobLead?.approvalInfo?.dropDown1}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Dropdown 2"
                  className={styles.TopContainer__Description}
                >
                  {jobLead?.approvalInfo?.dropDown2}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Dropdown 3"
                  className={styles.TopContainer__Description}
                >
                  {jobLead?.approvalInfo?.dropDown3}
                </Descriptions.Item>
              </Descriptions>
              <Title level={5}>{projectCoordinatesHeader}</Title>
              <Descriptions column={1} size="small" colon={false}>
                <Descriptions.Item
                  label="Job Site"
                  className={styles.TopContainer__Description}
                >
                  {jobLead?.jobSiteLatLng?.lng}° N /{" "}
                  {jobLead?.jobSiteLatLng?.lat}° W{" "}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Title level={5}>{onSiteContactHeader}</Title>
              <Descriptions column={1} size="small" colon={false}>
                <Descriptions.Item
                  label="Name"
                  className={styles.TopContainer__Description}
                >
                  {jobLead?.approvalInfo?.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Phone"
                  className={styles.TopContainer__Description}
                >
                  {jobLead?.approvalInfo?.phone}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Email"
                  className={styles.TopContainer__Description}
                >
                  {jobLead?.approvalInfo?.email}
                </Descriptions.Item>
              </Descriptions>
              <Title level={5}>{projectBondStatusHeader}</Title>
              <Descriptions column={1} size="small">
                <Descriptions.Item>
                  <ConditionalRender
                    visible={
                      jobLead?.approvalInfo?.projectBondStatus === "bonded"
                    }
                    fallback={<h4>{notBondedFallBack}</h4>}
                  >
                    <div
                      className={styles.FileName}
                      onClick={() => {
                        // if(cashJobInternalApproval)
                        openProjectBondPreview();
                      }}
                    >
                      <img
                        src={
                          iconMapping[bondInfoDocument?.fileType] || fileIcon
                        }
                        alt="PDF Icon"
                        className={styles.BondPDF}
                      />{" "}
                      {bondInfoDocument?.name ?? ""}
                    </div>
                  </ConditionalRender>
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={24}>
              <div>
                <span className={styles.SubHeader}>{invoiceNotesHeader}</span>
                <span className={styles.Title}>{invoiceNotesSubHeader}</span>
              </div>
              <div className={styles.NotesSection}>
                <div className={styles.Notes}>
                  {jobLead?.approvalInfo?.poInformation?.poInfo1}
                </div>
                <div className={styles.Notes}>
                  {jobLead?.approvalInfo?.poInformation?.poInfo2}
                </div>
                <div className={styles.Notes}>
                  {jobLead?.approvalInfo?.poInformation?.poInfo3}
                </div>
                <div className={styles.Notes}>
                  {jobLead?.approvalInfo?.poInformation?.poInfo4}
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      </div>
      {/* File Sections */}
      <div className={styles.BottomContainer}>
        <div className={styles.BottomContainer_Header}>
          <div className={styles.SubHeader}>
            {!!jobLead?.cashOrCreditSale
              ? cashCreditProjectDocuments?.length
                ? CASH_OR_CREDIT_DOCS_TITLE
                : NO_DOCUMENTS_TITLE
              : GENERAL_PROJECT_DOCS_TITLE}
          </div>
          <ConditionalRender
            visible={!!projectInformationSheet}
            fallback={
              !!!jobLead?.cashOrCreditSale ? (
                <h1>No project information sheet</h1>
              ) : null
            }
          >
            <div className={styles.FileName}>
              <img
                src={
                  iconMapping[projectInformationSheet?.type] ??
                  (iconMapping[projectInformationSheet?.fileType] || fileIcon)
                }
                alt={pdfAltText}
                className={styles.PdfIcon_ProjectInformationSheet}
              />{" "}
              {projectInformationSheet?.name ?? ""}
              <span>
                {" "}
                <Tag
                  className={clsx({
                    [styles.CustomTag_Approved]:
                      getOverallApprovalStatus() === SectionStatus.APPROVED,
                    [styles.CustomTag_Rejected]:
                      getOverallApprovalStatus() === SectionStatus.REJECTED,
                    [styles.CustomTag_Pending]:
                      getOverallApprovalStatus() === SectionStatus.PENDING,
                  })}
                >
                  {getOverallApprovalStatus()}
                </Tag>
              </span>
            </div>
          </ConditionalRender>
        </div>
        <div className={styles.BottomContainer_Sections}>
          <div className={styles.BottomContainer_Row}>
            <ConditionalRender 
              visible={!!!jobLead?.cashOrCreditSale}
            >
              <div className={styles.FixedLeftColumn}>
                {subSections?.map((item, index) => (
                  <div
                    key={index}
                    className={clsx({
                      [styles.SubSection_active]: selectedRow === index,
                      [styles.SubSection_Inactive]: selectedRow !== index,
                      [styles.LastRow]: index === subSections.length - 1,
                    })}
                    onClick={() => setSelectedRow(index)}
                  >
                    <div className={styles.LeftItem}>
                      {item}{" "}
                      <Tag
                        key={index}
                        className={clsx({
                          [styles.CustomTag_Approved]:
                            approvalDocuments?.[item]?.status ===
                              SectionStatus.L1_APPROVED ||
                            approvalDocuments?.[item]?.status ===
                              SectionStatus.L2_APPROVED,
                          [styles.CustomTag_Rejected]:
                            approvalDocuments?.[item]?.status ===
                              SectionStatus.L1_REJECTED ||
                            approvalDocuments?.[item]?.status ===
                              SectionStatus.L2_REJECTED,
                          [styles.CustomTag_Pending]:
                            !approvalDocuments ||
                            approvalDocuments?.[item]?.status ===
                              SectionStatus.PENDING,
                        })}
                      >
                        {approvalDocuments?.[item]?.status}
                      </Tag>
                    </div>
                    <div className={styles.RightItem}>
                      <ConditionalRender
                        visible={
                          approvalDocuments[item]?.status ===
                            SectionStatus.L1_REJECTED ||
                          approvalDocuments[item]?.status ===
                            SectionStatus.L2_REJECTED
                        }
                      >
                        <Popover
                          content={
                            approvalDocuments[item]?.review?.find(
                              (r) => r.status === SectionStatus.REJECTED,
                            )?.reasonForRejection
                          }
                          trigger="hover"
                          overlayClassName={styles.PopoverContainer}
                          placement={PlacementPositions.RIGHT_BOTTOM}
                        >
                          <img
                            src={popOverIcon}
                            alt="info"
                            className={styles.PopOverImg}
                          />
                        </Popover>
                      </ConditionalRender>
                    </div>
                  </div>
                ))}
              </div>
            </ConditionalRender>
            <div className={styles.PdfSection}>
              {selectedRow === 0 && (
                <div>
                  {projectInfoSectionFiles?.map((file, index) => (
                    <div key={index} className={styles.SectionPDFName}>
                      <img
                        src={iconMapping[file?.fileType] || fileIcon}
                        alt={pdfAltText}
                        className={styles.PdfIcon}
                      />{" "}
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
              {selectedRow === 1 && (
                <div>
                  {propertyLegalDescriptionFiles?.map((file, index) => (
                    <div key={index} className={styles.SectionPDFName}>
                      <img
                        src={iconMapping[file?.fileType] || fileIcon}
                        alt={pdfAltText}
                        className={styles.PdfIcon}
                      />{" "}
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
              {selectedRow === 2 && (
                <div>
                  {propertyOwnerInformationFiles?.map((file, index) => (
                    <div key={index} className={styles.SectionPDFName}>
                      <img
                        src={iconMapping[file?.fileType] || fileIcon}
                        alt={pdfAltText}
                        className={styles.PdfIcon}
                      />{" "}
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
              {selectedRow === 3 && (
                <div>
                  {generalContractorInformationFiles?.map((file, index) => (
                    <div key={index} className={styles.SectionPDFName}>
                      <img
                        src={iconMapping[file?.fileType] || fileIcon}
                        alt={pdfAltText}
                        className={styles.PdfIcon}
                      />{" "}
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
              {selectedRow === 4 && (
                <div>
                  {subcontractorInformationFiles?.map((file, index) => (
                    <div key={index} className={styles.SectionPDFName}>
                      <img
                        src={iconMapping[file?.fileType] || fileIcon}
                        alt={pdfAltText}
                        className={styles.PdfIcon}
                      />{" "}
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
              {selectedRow === 5 && (
                <div>
                  {projectBondFiles?.map((file, index) => (
                    <div key={index} className={styles.SectionPDFName}>
                      <img
                        src={iconMapping[file?.fileType] || fileIcon}
                        alt={pdfAltText}
                        className={styles.PdfIcon}
                      />{" "}
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewInternalApproval;
